import ChoicePicker from '../components/ChoicePicker';
import TextInput from '../components/TextInput';
import ContinueButtonBar from '../components/ContinueButtonBar';
import type { useCareerUpConfirmationFlow } from './useCareerUpConfirmationFlow';
import type { CareerUpConfirmationStep, YesNo } from './careerUpConfirmationTypes';

type Flow = ReturnType<typeof useCareerUpConfirmationFlow>;

const HAS_CARD_OPTIONS = (t: Flow['t']) => [
  { value: 'yes' as const, label: t('submitDocuments.careerUpConfirmation.hasCardYes') },
  { value: 'no' as const, label: t('submitDocuments.careerUpConfirmation.hasCardNo') },
];

const UPLOAD_STATUS_OPTIONS = (t: Flow['t']) => [
  { value: 'yes' as const, label: t('submitDocuments.careerUpConfirmation.alreadyUploadedYes') },
  { value: 'no' as const, label: t('submitDocuments.careerUpConfirmation.notYetNo') },
];

function choiceValue(
  values: Flow['values'],
  stepId: CareerUpConfirmationStep
): YesNo | null {
  if (stepId === 'hasCard') {
    return values.hasCareerUpCard === 'yes' || values.hasCareerUpCard === 'no' ? values.hasCareerUpCard : null;
  }
  if (stepId === 'careerUpCardUploaded') {
    return values.careerUpCardUploaded === 'yes' || values.careerUpCardUploaded === 'no'
      ? values.careerUpCardUploaded
      : null;
  }
  if (stepId === 'driversLicenseUploaded') {
    return values.driversLicenseUploaded === 'yes' || values.driversLicenseUploaded === 'no'
      ? values.driversLicenseUploaded
      : null;
  }
  return null;
}

export default function CareerUpConfirmationChatComposer({ flow }: { flow: Flow }) {
  const {
    currentStep,
    t,
    values,
    advanceHasCard,
    advanceCareerUpCardUploaded,
    advanceDriversLicenseUploaded,
    advancePassword,
    passwordDraft,
    setPasswordDraft,
  } = flow;
  if (!currentStep) return null;

  if (currentStep.stepId === 'hasCard') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={choiceValue(values, 'hasCard')}
          options={HAS_CARD_OPTIONS(t)}
          onChange={(v) => void advanceHasCard(v as YesNo)}
        />
      </div>
    );
  }

  if (currentStep.stepId === 'careerUpCardUploaded') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={choiceValue(values, 'careerUpCardUploaded')}
          options={UPLOAD_STATUS_OPTIONS(t)}
          onChange={(v) => void advanceCareerUpCardUploaded(v as YesNo)}
        />
      </div>
    );
  }

  if (currentStep.stepId === 'driversLicenseUploaded') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={choiceValue(values, 'driversLicenseUploaded')}
          options={UPLOAD_STATUS_OPTIONS(t)}
          onChange={(v) => void advanceDriversLicenseUploaded(v as YesNo)}
        />
      </div>
    );
  }

  if (currentStep.stepId === 'cardPassword') {
    return (
      <TextInput
        fields={[
          {
            id: 'cardPassword',
            hideLabel: true,
            ariaLabel: t('submitDocuments.careerUpConfirmation.steps.cardPassword'),
            placeholder: t('submitDocuments.careerUpConfirmation.passwordPlaceholder'),
            autoComplete: 'off',
            required: true,
            maxLength: 64,
          },
        ]}
        values={{ cardPassword: passwordDraft }}
        onChange={(_, v) => setPasswordDraft(v)}
        onSubmit={() => void advancePassword()}
        submitAriaLabel={t('submitDocuments.chat.send')}
      />
    );
  }

  return null;
}

export function CareerUpConfirmationDeclinedComposer({ onContinue }: { onContinue: () => void }) {
  return <ContinueButtonBar onContinue={onContinue} />;
}
